import { RealisationHourtype } from '../../app/classes/realisation';
import { Environment, PeriodType } from '../../app/environment';
import { defaultHourtypeLabels } from '../hourtype-labels';
import { entityTypes } from './entitytypes';
import { Roles } from './roles';
import { PlanningStatus } from './status.planning';
import { TEMPLATE_MAP } from './templates/template-registry';

export const envBasics: Environment = {
    allowWorksWith: true,
    apiEndpoint: '',
    appTitle: 'Track Line',
    asphaltexecutor: null,
    asphaltPlanning: false,
    companyAddress: {
        street: 'Handelstraat',
        housenumber: '2',
        zipcode: '4143 HT',
        town: 'Leerdam',
    },
    checkHoursBundle: 'planning_id',
    dayReportPlanning: false,
    defaultStand: false,
    disableAfas: true,
    employeeEmployees: true,
    employeePlanning: true,
    enableDayreport: true,
    entitytypes: entityTypes,
    entitytypeTemplates: TEMPLATE_MAP,
    executor: Roles.Foreman,
    executorName: 'voorman',
    executorNamePlural: 'voormannen',
    executorPlanning: false,
    executors: [Roles.Foreman],
    feEndpoint: 'http://localhost:4204/',
    fromEmail: 'planning@trackline.nl',
    hourtypeLabels: {
        ...defaultHourtypeLabels,
        [RealisationHourtype.driving_back]: 'Terugreis van locatie',
        [RealisationHourtype.driving_to]: 'Heenreis naar locatie',
        [RealisationHourtype.time_for_time]: 'Spaaruren',
        [RealisationHourtype.worktime]: 'Productie',
    },
    inventarizePlanning: true,
    leaveHourTypes: [
        RealisationHourtype.illness,
        RealisationHourtype.time_for_time,
        RealisationHourtype.paid_leave,
        RealisationHourtype.short_absence,
        RealisationHourtype.education,
        RealisationHourtype.bank_holiday,
        RealisationHourtype.special_leave,
        RealisationHourtype.additional_parental,
    ],
    microsoftLogin: true,
    name: 'Track Line B.V.',
    officePhoneNumber: '+31345523893',
    onlyOvertime: false,
    orderDirect: true,
    pauseThresholds: {
        default: { 360: 30, 525: 45, 660: 60 },
        company: { 195: 15, 390: 30, 480: 60 },
    },
    pauseEditableHourTypes: [
        RealisationHourtype.worktime,
        RealisationHourtype.office,
        RealisationHourtype.waiting,
        RealisationHourtype.rain,
        RealisationHourtype.setting_out,
    ],
    worktimeHourTypes: [
        RealisationHourtype.worktime,
        RealisationHourtype.driving_to,
        RealisationHourtype.driving_back,
        RealisationHourtype.office,
        RealisationHourtype.waiting,
        RealisationHourtype.rain,
        RealisationHourtype.setting_out,
    ],
    subProjects: false,
    periodType: PeriodType.Month,
    periodSettlementColums: {
        '135V': false,
        '145V': false,
        '200V': true,
        100: false,
        125: false,
        135: false,
        145: false,
        150: true,
        200: true,
        '200Z': true,
        Z: false,
    },
    planningEntityTypes: [
        entityTypes.markings,
        entityTypes.blast,
        entityTypes.wipe,
        entityTypes.inventorize,
        entityTypes.workplace,
    ],
    planningTableDialog: 'trackline',
    platform: 'trackline',
    production: false,
    projectIdentifier: /\b(\d{4,})\b/,
    projectmanager: Roles.Projectmanager,
    // General KAM reports funnel into DvdS, not per subsidiary, so this stays disabled here.
    publicTicketForm: false,
    realisationHourTypes: [
        RealisationHourtype.loading_unloading,
        RealisationHourtype.driving_to,
        RealisationHourtype.worktime,
        RealisationHourtype.driving_back,
        RealisationHourtype.loading_unloading_end,
        RealisationHourtype.repair_maintenance,
        RealisationHourtype.waiting,
        RealisationHourtype.rain,
        RealisationHourtype.setting_out,
        RealisationHourtype.office,
        RealisationHourtype.time_for_time,
        RealisationHourtype.paid_leave,
        RealisationHourtype.short_absence,
        RealisationHourtype.sleep,
        RealisationHourtype.special_leave,
        RealisationHourtype.education,
    ],
    roles: {
        AfasAuth: Roles.AfasAuth,
        AiReports: Roles.AiReports,
        Codalt: Roles.Codalt,
        Employee: Roles.Employee,
        EmployeeBlast: Roles.EmployeeBlast,
        EmployeeInventorize: Roles.EmployeeInventorize,
        EmployeeMarkings: Roles.EmployeeMarkings,
        EmployeeWipe: Roles.EmployeeWipe,
        Foreman: Roles.Foreman,
        FormerEmployee: Roles.FormerEmployee,
        Hourcheck: Roles.Hourcheck,
        HoursExtended: Roles.HoursExtended,
        Hourwriter: Roles.Hourwriter,
        Hrm: Roles.Hrm,
        Kam: Roles.Kam,
        Leave: Roles.Leave,
        LeaveManagement: Roles.LeaveManagement,
        Management: Roles.Management,
        Planner: Roles.Planner,
        PlanningAlwaysRead: Roles.PlanningAlwaysRead,
        ProjectManagement: Roles.ProjectManagement,
        Projectmanager: Roles.Projectmanager,
        UserManagement: Roles.UserManagement,
    } as const,
    rootPage: 'weekplanning',
    rootPageName: 'Weekplanning',
    shortName: 'Track Line',
    status: {
        Planning: {
            PlanningBehoefte: PlanningStatus.PlanningBehoefte,
            PlanningGarantiewerk: PlanningStatus.PlanningGarantiewerk,
            PlanningGeblokkeerd: PlanningStatus.PlanningGeblokkeerd,
            PlanningGepland: PlanningStatus.PlanningGepland,
            PlanningBevestigd: PlanningStatus.PlanningBevestigd,
            PlanningRegen: PlanningStatus.PlanningRegen,
            PlanningReservering: PlanningStatus.PlanningReservering,
            PlanningSlaapdag: PlanningStatus.PlanningSlaapdag,
            PlanningVerwerkt: PlanningStatus.PlanningVerwerkt,
        },
    } as const,
    test: false,
    titleAppend: ' - Track Line B.V.',
    transportPlanning: false,
    travelTime: false,
    vkmPlanning: false,
    weekPlanning: true,
    weekPlanningEmployees: [Roles.EmployeeBlast, Roles.EmployeeMarkings, Roles.EmployeeWipe, Roles.EmployeeInventorize],
    weekScheduleComponent: 'planning',
    wsEndpoint: 'ws://localhost:1340',
};
